import { useEffect } from 'react';
import {
  SHOPPING_NUMBER_BG_SPRITE,
  SHOPPING_NUMBER_BG_SYMBOL_ID,
  SHOPPING_NUMBER_BG_USE_HTML,
} from './numberBgSvg';
import styles from './shoppingNumberBg.module.scss';

const NUMBER_SELECTOR = '.subbuzz__number';
const BG_CLASS = 'js-shopping-number-bg';
const SPRITE_HOLDER_ID = 'js-shopping-number-bg-sprite';
const WRAPPER_CLEAR_CLASS = 'js-shopping-number-clear';
const NO_WRAP_CLASS = 'js-shopping-number-no-wrap';
const TITLE_TEXT_SELECTOR = '.js-subbuzz__title-text';
// Numbered headings without a header render link_name directly inside this link.
const LINK_NAME_SELECTOR = ':scope > a';

const IMAGES_CONTAINER_ATTR = 'data-shopping-images-container';
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT']);

const getNextVisualSibling = (el) => {
  let sibling = el?.nextElementSibling;
  while (sibling && SKIP_TAGS.has(sibling.tagName)) sibling = sibling.nextElementSibling;
  return sibling;
};

// Photoset headings are wrapped in their own div, so walk up until something follows.
const getFollowingBlock = (heading, container) => {
  let el = heading;
  while (el && el !== container) {
    const sibling = getNextVisualSibling(el);
    if (sibling) return sibling;
    el = el.parentElement;
  }
  return null;
};

// Chrome returns one rect per child fragment of an inline, not per line, so compare vertical positions.
const isSingleLine = (el) => {
  const rects = Array.from(el.getClientRects()).filter((r) => r.width > 0 && r.height > 0);
  if (rects.length === 0) return false;
  const [first] = rects;
  return rects.every((r) => {
    const mid = (r.top + r.bottom) / 2;
    return mid > first.top && mid < first.bottom;
  });
};

const ensureSpriteInjected = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(SHOPPING_NUMBER_BG_SYMBOL_ID)) return;
  const holder = document.createElement('div');
  holder.id = SPRITE_HOLDER_ID;
  holder.setAttribute('aria-hidden', 'true');
  holder.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden';
  holder.innerHTML = SHOPPING_NUMBER_BG_SPRITE;
  document.body.appendChild(holder);
};

const buildBg = () => {
  const span = document.createElement('span');
  span.className = `${BG_CLASS} ${styles.numberBg}`;
  span.setAttribute('aria-hidden', 'true');
  span.innerHTML = SHOPPING_NUMBER_BG_USE_HTML;
  return span;
};

/**
 * Injects a decorative SVG background behind every `.subbuzz__number`
 * inside `containerRef`, cycling through three color variants.
 */
export default function useShoppingNumberBg(containerRef, enabled) {
  useEffect(() => {
    if (!enabled) return undefined;
    const container = containerRef?.current;
    if (!container) return undefined;

    ensureSpriteInjected();

    const variantClasses = [
      styles.variant0,
      styles.variant1,
      styles.variant2,
    ].filter(Boolean);

    // Measures heading line-wrap state and applies layout styles.
    // Extracted so it can be called independently on resize without
    // re-injecting the SVG background.
    const measureAndApplyLayout = (numberEl) => {
      const heading = numberEl.closest('h1, h2, h3, h4, h5, h6');
      // Rect count of the inline title element = rendered lines, independent of the number's layout.
      const titleText =
        heading?.querySelector(TITLE_TEXT_SELECTOR) || heading?.querySelector(LINK_NAME_SELECTOR);
      const isSingleLineTitle = !!titleText && isSingleLine(titleText);

      if (isSingleLineTitle) numberEl.classList.add(NO_WRAP_CLASS);
      else numberEl.classList.remove(NO_WRAP_CLASS);

      const wrapper = getFollowingBlock(heading, container);
      if (!wrapper || wrapper.tagName !== 'DIV') {
        if (wrapper instanceof HTMLElement) {
          wrapper.style.clear = 'left';
        }
        return;
      }

      wrapper.classList.add(WRAPPER_CLEAR_CLASS);

      wrapper.style.clear = 'left';
      wrapper.style.width = '100%';
      wrapper.style.display = 'flow-root';
      wrapper.style.marginLeft = '0';
      wrapper.style.marginTop = '0';

      // Find the images container using a stable data attribute if already tagged,
      // otherwise fall back to substring match and tag it for future lookups.
      let imagesContainer = wrapper.querySelector(`[${IMAGES_CONTAINER_ATTR}]`);
      if (!imagesContainer) {
        imagesContainer = wrapper.querySelector("[class*='images_container']");
        if (imagesContainer) imagesContainer.setAttribute(IMAGES_CONTAINER_ATTR, '');
      }
      if (imagesContainer && imagesContainer instanceof HTMLElement) {
        imagesContainer.style.clear = 'left';
        imagesContainer.style.width = '100%';
        imagesContainer.style.marginLeft = '0';
        imagesContainer.style.marginTop = '0';
      }
    };

    const decorate = (numberEl, index) => {
      const variant = variantClasses[index % variantClasses.length];
      // Skip class mutations when already correct to avoid triggering sibling observers.
      if (!numberEl.classList.contains(variant)) {
        numberEl.classList.remove(...variantClasses);
        numberEl.classList.add(variant);
      }

      // Skip already-decorated elements — only inject SVG + measure for new ones.
      if (numberEl.querySelector(`.${BG_CLASS}`)) return;
      numberEl.classList.add(styles.numberWrapper);
      numberEl.insertBefore(buildBg(), numberEl.firstChild);

      measureAndApplyLayout(numberEl);
    };

    const sync = () => {
      Array.from(container.querySelectorAll(NUMBER_SELECTOR)).forEach(decorate);
    };

    sync();

    // Re-evaluate line wrapping on resize since heading line breaks can change across breakpoints.
    // Only re-measures layout; does not re-inject SVG backgrounds.
    const handleResize = () => {
      Array.from(container.querySelectorAll(NUMBER_SELECTOR)).forEach(measureAndApplyLayout);
    };
    let resizeRaf = null;
    const onResize = () => {
      if (resizeRaf) return;
      resizeRaf = requestAnimationFrame(() => {
        resizeRaf = null;
        handleResize();
      });
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', onResize, { passive: true });
    }

    if (typeof MutationObserver === 'undefined') {
      return () => {
        if (typeof window !== 'undefined') window.removeEventListener('resize', onResize);
        if (resizeRaf) cancelAnimationFrame(resizeRaf);
      };
    }

    let scheduled = false;
    const observerOptions = { childList: true, subtree: true };
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        observer.disconnect();
        sync();
        observer.observe(container, observerOptions);
      });
    });
    observer.observe(container, observerOptions);

    const cleanup = () => {
      observer.disconnect();
      if (typeof window !== 'undefined') window.removeEventListener('resize', onResize);
      if (resizeRaf) cancelAnimationFrame(resizeRaf);

      // Revert all DOM mutations made by decorate/measureAndApplyLayout.
      Array.from(container.querySelectorAll(NUMBER_SELECTOR)).forEach((numberEl) => {
        // Remove injected SVG background spans.
        numberEl.querySelectorAll(`.${BG_CLASS}`).forEach((el) => el.remove());
        // Remove added classes.
        numberEl.classList.remove(styles.numberWrapper, NO_WRAP_CLASS, ...variantClasses);

        // Revert wrapper inline styles.
        const heading = numberEl.closest('h1, h2, h3, h4, h5, h6');
        const wrapper = getFollowingBlock(heading, container);
        if (wrapper instanceof HTMLElement && wrapper.tagName !== 'DIV') {
          wrapper.style.clear = '';
        }
        if (wrapper && wrapper.tagName === 'DIV') {
          wrapper.classList.remove(WRAPPER_CLEAR_CLASS);
          wrapper.style.clear = '';
          wrapper.style.width = '';
          wrapper.style.display = '';
          wrapper.style.marginLeft = '';
          wrapper.style.marginTop = '';

          const imagesContainer = wrapper.querySelector(`[${IMAGES_CONTAINER_ATTR}]`);
          if (imagesContainer && imagesContainer instanceof HTMLElement) {
            imagesContainer.style.clear = '';
            imagesContainer.style.width = '';
            imagesContainer.style.marginLeft = '';
            imagesContainer.style.marginTop = '';
            imagesContainer.removeAttribute(IMAGES_CONTAINER_ATTR);
          }
        }
      });
    };

    return cleanup;
  }, [containerRef, enabled]);
}
